import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import {type PaginatedResult, normalizePagination} from "@/utils/pagination";

export interface Wallet {
  id: string;
  userId: string;
  userEmail: string;
  currency: string;
  balance: number;
  availableBalance: number;
  frozenBalance: number;
  status: "active" | "frozen" | "closed";
  createdAt: string;
  updatedAt: string;
}

interface WalletFilters {
  page?: number;
  limit?: number;
  userId?: string;
  currency?: string;
  status?: string;
  search?: string;
}

export type WalletListResult = PaginatedResult<Wallet>;

export interface WalletStats {
  totalWallets: number;
  activeWallets: number;
  totalBalanceUsd: number;
  frozenWallets: number;
}

export const getWalletsService = async (filters: WalletFilters = {}): Promise<TResponseRequest<WalletListResult>> => {
  const raw = await httpRequest.get<Wallet[]>({
    url: buildQueryUrl(`${config.walletApiUrl}/bo/wallets`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getWalletByIdService = async (id: string): Promise<TResponseRequest<Wallet>> => {
  return httpRequest.get<Wallet>({
    url: `${config.walletApiUrl}/bo/wallets/${encodeURIComponent(id)}`,
  });
};

export const getWalletStatsService = async (): Promise<TResponseRequest<WalletStats>> => {
  return httpRequest.get<WalletStats>({
    url: `${config.walletApiUrl}/bo/wallets/stats`,
  });
};

export const freezeWalletService = async (id: string): Promise<TResponseRequest<void>> => {
  return httpRequest.post<void>({
    url: `${config.walletApiUrl}/bo/wallets/${encodeURIComponent(id)}/freeze`,
    body: {},
  });
};

export const unfreezeWalletService = async (id: string): Promise<TResponseRequest<void>> => {
  return httpRequest.post<void>({
    url: `${config.walletApiUrl}/bo/wallets/${encodeURIComponent(id)}/unfreeze`,
    body: {},
  });
};
